import React from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { ReactComponent as ContactBookSVG } from "../../../../../../images/svg/organization/contact-book.svg";
import { OrganizationApi, OrganizationFormProps } from "../../../../../../services/Api/types";
import TaxIdNoWithGusInput from "../../../../components/Admin/TaxIdNoWithGusInput/TaxIdNoWithGusInput";

interface Props {
	form: UseFormReturn<OrganizationFormProps>;
	canEdit: (dataType?: "organization" | "company" | "contact") => boolean | undefined;
	organization: OrganizationApi;
	getAllCountryOptions: (option: Record<string, string>) => Record<string, string>[];
}

const OrganizationAdminCompanyInfoForm = ({ form, canEdit, getAllCountryOptions, organization }: Props) => {
	const { t } = useTranslation("admin");
	const {
		formState: { errors },
		register,
		control,
	} = form;

	return (
		<fieldset className="form-group no-gutters">
			<div className={"row"}>
				<div className={"col-md-12"}>
					<div className="name-input">
						<FormInput
							label={t("modules.organization.field.organization_name.title")}
							name={"name"}
							errors={errors}
							placeholder={t("modules.organization.field.organization_name.placeholder")}
							register={register}
						/>
						<ContactBookSVG />
					</div>
				</div>
			</div>
			<h5 className="mt-4">{t("common.word.company_info")}</h5>
			<div className="row">
				<div className="col-md-8">
					<FormInput
						label={t("common.word.company_name")}
						name="company_name"
						errors={errors}
						disabled={!canEdit("company")}
						register={register}
					/>
				</div>
				<div className="col-md-4">
					<TaxIdNoWithGusInput
						prefix={"address."}
						form={form}
						name={"tax_id_no"}
						disabled={!canEdit("company")}
					/>
				</div>
			</div>
			<div className="row">
				<div className="col-md-8">
					<div className="row">
						<div className="col-md-6">
							<FormInput
								label={t("lib:common.address.street")}
								name="address.street"
								errors={errors}
								disabled={!canEdit("company")}
								register={register}
							/>
						</div>
						<div className="col-md-3">
							<FormInput
								label={t("lib:common.address.build_nr")}
								register={register}
								name="address.build_nr"
								disabled={!canEdit("company")}
								errors={errors}
							/>
						</div>
						<div className="col-md-3">
							<FormInput
								label={t("lib:common.address.flat_nr")}
								register={register}
								name="address.flat_nr"
								disabled={!canEdit("company")}
								errors={errors}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<FormInput
								label={t("lib:common.address.city")}
								register={register}
								name="address.city"
								disabled={!canEdit("company")}
								errors={errors}
							/>
						</div>
						<div className="col-md-3">
							<FormInput
								label={t("lib:common.address.zip_code")}
								register={register}
								name="address.zip_code"
								disabled={!canEdit("company")}
								errors={errors}
							/>
						</div>
						<div className="col-md-3">
							<FormSelectGroup
								errors={errors}
								control={control}
								label={t("lib:common.address.country")}
								name="address.country"
								disabled={!canEdit("company")}
								options={getAllCountryOptions({
									label: organization?.company_country,
									value: organization?.company_country,
								})}
								data-testid="address.country"
							/>
						</div>
					</div>
				</div>
				<div className="col-md-4">
					<div>
						<FormInput
							label={t("lib:common.word.phone_number")}
							register={register}
							name="contact.phone_number"
							disabled={!canEdit("company")}
							errors={errors}
						/>
					</div>
					<div>
						<FormInput
							label={t("lib:common.word.email")}
							register={register}
							name="contact.email"
							help={t("modules.organization.field.email.help_text")}
							disabled={!canEdit("company")}
							errors={errors}
						/>
					</div>
				</div>
			</div>
		</fieldset>
	);
};

export default OrganizationAdminCompanyInfoForm;
